<template>
  <!-- 
   Wrap-content mode: wraps some content (text/dom) with a clickable invisible-ish button
   - Shows copy icon on hover
   - Clicking on entire wrapped content copies the value attribute into clipboard
   - Hovering over the button, shows the icon as a "clue" that it's copy-able
   - a light dotted underline is also another "clue" "tw-border-b-2 tw-border-dotted tw-border-slate-300"
  -->
  <button v-if="mode === 'wrap-content'"
    type="button"
    class="tw-relative tw-inline-flex tw-items-center tw-justify-center focus:tw-bg-indigo-500/20 focus:tw-border-rounded"
    style="border-bottom: 1px dotted #ccc"
    :title="copied ? 'Copied' : title"
    :aria-label="ariaLabel"
    @click.prevent.stop="copy"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <slot></slot>
    <font-awesome-icon v-if="isHovered"
      icon="copy" 
      class="tw-h-3.5 tw-w-3.5 tw-absolute tw-z-100 tw-text-slate-400" 
      :class="iconPosition === 'left' ? '-tw-left-4' : '-tw-right-4'"
    />
  </button>
  <!-- Icon-only mode: a single clickable icon that copies value prop into clipboard -->
  <button v-else
    type="button"
    class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white/60 tw-px-2 tw-py-1 tw-text-slate-600 hover:tw-bg-slate-200 hover:tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/20"
    :title="copied ? 'Copied' : title"
    :aria-label="ariaLabel"
    @click="copy"
  >
    <font-awesome-icon icon="copy" class="tw-h-3.5 tw-w-3.5" />
  </button>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { pushToast } from '@/lib/toast';

const props = defineProps({
  value: String,
  title: String,
  ariaLabel: String,
  mode: {
    type: String,
    default: 'icon',
    validate: (value) => ['wrap-content', 'icon'].includes(value),
  },
  // for wrap-content mode
  iconPosition: {
    type: String,
    default: 'right',
    validate: (value) => ['left', 'right'].includes(value),
  },
});

const copied = ref(false);
const isHovered = ref(false);
async function copy() {
  const text = props.value ?? '';

  try {
    // preferred API for clipboard
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    copied.value = true;
    pushToast('Copied to clipboard!', 'success', 1000);
    window.setTimeout(() => {
      copied.value = false;
    }, 1200);
  } catch (e) {
    console.error('Copy failed', e);
  }
}

const title = props.title ?? 'Copy';
const ariaLabel = props.ariaLabel ?? 'Copy to clipboard';
</script>
